import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { FileText, Mail, Image, FolderOpen, StickyNote, User, FileSpreadsheet } from "lucide-react";

const SCATTERED = [
  { label: "Manual.pdf", icon: FileText, x: -160, y: -110, r: -8 },
  { label: "Esquema eléctrico", icon: FileSpreadsheet, x: 150, y: -130, r: 6 },
  { label: "Email del fabricante", icon: Mail, x: -190, y: 30, r: 5 },
  { label: "Fotografía", icon: Image, x: 185, y: 10, r: -6 },
  { label: "Carpeta de red", icon: FolderOpen, x: -120, y: 130, r: 7 },
  { label: "Notas", icon: StickyNote, x: 110, y: 140, r: -5 },
  { label: "«Lo sabe Carlos»", icon: User, x: 0, y: -170, r: 3 },
];

export default function Problem() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section className="bg-white border-y border-zinc-100 overflow-hidden" data-testid="problema-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 lg:py-36">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-widest font-medium text-zinc-500 mb-5">El problema</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight">
            La información de tus máquinas existe.
            <br />
            <span className="text-zinc-400">El problema es encontrarla.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mt-16">
          <div
            ref={ref}
            className="relative h-[380px] sm:h-[420px] flex items-center justify-center"
            data-testid="document-convergence-animation"
          >
            {SCATTERED.map((chip, i) => (
              <motion.div
                key={chip.label}
                initial={{ x: chip.x, y: chip.y, rotate: chip.r, opacity: 0 }}
                animate={
                  inView
                    ? { x: [chip.x, chip.x * 0.25, 0], y: [chip.y, chip.y * 0.25, 0], rotate: [chip.r, chip.r / 2, 0], opacity: [0, 1, 0] }
                    : {}
                }
                transition={{ duration: 2.2, delay: 0.15 + i * 0.12, times: [0, 0.55, 1], ease: "easeInOut" }}
                className="absolute inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-zinc-200 shadow-sm text-sm text-zinc-600 whitespace-nowrap"
              >
                <chip.icon size={15} className="text-zinc-400" />
                {chip.label}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 2.4 }}
              className="relative z-10 w-64 sm:w-72 bg-white rounded-3xl border border-zinc-200 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.2)] p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-emerald-700">Operativa</span>
              </div>
              <p className="font-heading font-bold text-xl tracking-tight">INYECTORA 01</p>
              <p className="text-sm text-zinc-500 mt-1">ENGEL · victory 220</p>
              <div className="mt-5 space-y-2">
                {["Información", "Documentos", "Fotografías", "Historial"].map((s, i) => (
                  <motion.div
                    key={s}
                    initial={{ opacity: 0, x: -8 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 2.7 + i * 0.15 }}
                    className="flex items-center justify-between rounded-xl bg-zinc-50 border border-zinc-100 px-3.5 py-2.5"
                  >
                    <span className="text-sm font-medium text-zinc-800">{s}</span>
                    <span className="text-xs text-zinc-400">✓</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="space-y-6 text-lg text-zinc-600 leading-relaxed max-w-lg">
            <p>
              Manuales en carpetas. Esquemas en otro ordenador. Fotografías en el móvil de un
              técnico. Intervenciones que nadie documentó. Información que solo conoce una persona.
            </p>
            <p>Cuando hace falta algo, comienza la búsqueda.</p>
            <p className="text-zinc-900 font-medium">
              Reunimos toda esa información alrededor de aquello a lo que realmente pertenece:
              la máquina.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
